import { Injectable } from '@angular/core';
import { Observable, forkJoin } from 'rxjs';
import { map } from 'rxjs/operators';
import { TicketClientApiService } from 'src/app/core/service/apis/tickets/ticket-client-api.service';
import { TicketMapperService } from 'src/app/shared/service/ticket-client/tickets-mapper.service';
import { ActorType, Ticket, TicketCategory } from 'src/app/shared/models/tickets/ticket.model';
import { TicketBcpRepository } from '../../domain/repository/ticket-bcp.repository';
import { TicketBcpRegisterPayload } from '../../domain/models/ticket-bcp.model';
import { DocumentApiService } from 'src/app/core/service/apis/files/document-api.service';
import { DOMAIN_TYPE_SUPPORT } from 'src/app/shared/models/documents/documents.model';
import { AttachmentType } from 'src/app/shared/constants/attachment-type.constants';
import { TicketFieldUpdateService } from 'src/app/shared/service/ticket-client/ticket-field-update.service';

@Injectable()
export class HttpTicketBcpRepository implements TicketBcpRepository {
  constructor(
    private readonly mapper: TicketMapperService,
    private readonly ticketsApi: TicketClientApiService,
    private readonly documentApi: DocumentApiService,
    private readonly fieldUpdate: TicketFieldUpdateService,
  ) { }

  /** No hay endpoint de detalle: se reusa la búsqueda del kanban filtrando por el id del ticket. */
  /** Detalle + adjuntos. `refundAmount`/`advisorCrece` viven en `metadata`. */
  getDetail(recordId: string): Observable<Ticket | null> {
    return forkJoin({
      detail: this.ticketsApi.getTicketDetail(recordId),
      docs: this.documentApi.searchDocuments({
        domainType: DOMAIN_TYPE_SUPPORT, recordId, attachmentType: AttachmentType.ADJUNTOS,
      }),
    }).pipe(
      map(({ detail, docs }) => (detail?.ticketRecordId
        ? { ...this.mapper.mapTicketDetail(detail, 'bcp'), attachedDocument: this.mapper.mapTicketDocuments(docs) }
        : null)),
    );
  }

  register(payload: TicketBcpRegisterPayload): Observable<{ recordIdTicket: string }> {
    return this.ticketsApi.createTicket({
      recordIdConstitucion: payload.constitutionRecordId,
      tipoIncidencia: this.mapper.reverseMapIncidentTypes(payload.incidentType),
      descripcion: payload.description,
      categoriaTicket: TicketCategory.BCP,
      actorCreador: ActorType.CC_AGENT,
      asesorAsignado: payload.assignedAdvisor,
      metadata: {
        refundAmount: payload.transferredAmount,
        advisorCrece: payload.advisorCrece,
      },
    }).pipe(
      map(response => ({ recordIdTicket: response.ticketRecordId })),
    );
  }

  updateStatus(recordId: string, newStatus: string, actorType: string, reportedBy: string): Observable<void> {
    return this.ticketsApi.updateTicketStatus(recordId, {
      newStatus,
      reportedBy,
      actorType,
      actionType: 'STATUS_CHANGE',
    }).pipe(map(() => undefined));
  }

  updateStringField(recordId: string, field: string, value: any): Observable<void> {
    return this.fieldUpdate.updateField(recordId, field, field === 'tipoIncidencia' ? this.mapper.reverseMapIncidentTypes(value) : value);
  }
}
